import { Capacitor } from '@capacitor/core'
import { Share } from '@capacitor/share'
import { toast } from 'sonner'
import { classifyError } from './errors'
import { toastError } from './toast'
import { track, type AnalyticsEvents } from './analytics'
import { getShareOrigin } from './utils'
import { isShareCancel } from './shareCancel'
import { writeClipboard } from './clipboard'
import { log } from './logger'
import { holdOtaSwap } from './otaSafeToSwap'
import {
  inviteLinkCopiedToast, inviteLinkCopyErrorToast,
  eventLinkCopiedToast, eventLinkCopyErrorToast,
} from './commonStrings'

export function canNativeShare(): boolean {
  return Capacitor.isNativePlatform() || typeof navigator.share === 'function'
}

interface CopyLinkOpts<E extends keyof AnalyticsEvents> {
  successToast: string
  errorToast: string
  /** Omit when the surface already tracked the share at a better moment. */
  event?: E
  params?: AnalyticsEvents[E]
}

export async function copyLink<E extends keyof AnalyticsEvents>(
  url: string,
  opts: CopyLinkOpts<E>,
): Promise<void> {
  try {
    await writeClipboard(url)
    toast.success(opts.successToast)
    if (opts.event) track(opts.event, opts.params as AnalyticsEvents[E])
  } catch (err) {
    toastError(opts.errorToast, classifyError(err))
  }
}

interface ShareLinkOpts<E extends keyof AnalyticsEvents> {
  text?: string
  successToast: string
  errorToast: string
  event: E
  shareParams: AnalyticsEvents[E]
  copyParams: AnalyticsEvents[E]
}

export async function shareLink<E extends keyof AnalyticsEvents>(
  url: string,
  opts: ShareLinkOpts<E>,
): Promise<void> {
  // Android stops the activity once a share target is picked; a background OTA
  // swap there would drop the result.
  const release = holdOtaSwap()
  try {
    if (Capacitor.isNativePlatform()) {
      await Share.share({ text: opts.text, url })
    } else {
      await navigator.share({ text: opts.text, url })
    }
    track(opts.event, opts.shareParams)
  } catch (err) {
    if (isShareCancel(err)) return
    log('share', 'share rejected', { error: String(err), native: Capacitor.isNativePlatform() })
    await copyLink(url, {
      successToast: opts.successToast,
      errorToast: opts.errorToast,
      event: opts.event,
      params: opts.copyParams,
    })
  } finally {
    release()
  }
}

type ShareSource =
  | 'group_card'
  | 'group_invite_dialog'
  | 'friend_social'
  | 'friend_card'
  | 'festival_add_people'

export async function copyInviteLink(url: string, source: ShareSource): Promise<void> {
  await copyLink(url, {
    successToast: inviteLinkCopiedToast(),
    errorToast: inviteLinkCopyErrorToast(),
    event: 'invite_shared',
    params: { method: 'clipboard', entry_point: source },
  })
}

export async function nativeShareInvite(url: string, source: ShareSource): Promise<void> {
  await shareLink(url, {
    successToast: inviteLinkCopiedToast(),
    errorToast: inviteLinkCopyErrorToast(),
    event: 'invite_shared',
    shareParams: { method: 'native_share', entry_point: source },
    copyParams: { method: 'clipboard', entry_point: source },
  })
}

export async function copyEventLink(eventId: string): Promise<void> {
  const url = `${getShareOrigin()}/events/${eventId}`
  await copyLink(url, {
    successToast: eventLinkCopiedToast(),
    errorToast: eventLinkCopyErrorToast(),
    event: 'event_shared',
    params: { method: 'clipboard', event_id: eventId },
  })
}

export async function nativeShareEvent(eventId: string, eventName: string): Promise<void> {
  const url = `${getShareOrigin()}/events/${eventId}`
  await shareLink(url, {
    text: eventName,
    successToast: eventLinkCopiedToast(),
    errorToast: eventLinkCopyErrorToast(),
    event: 'event_shared',
    shareParams: { method: 'native_share', event_id: eventId },
    copyParams: { method: 'clipboard', event_id: eventId },
  })
}
